import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import YearSelector from './YearSelector';
import NotificationDropdown from './NotificationDropdown';
import api from '../services/api';
import {
  FiHome,
  FiUsers,
  FiBookOpen,
  FiUserCheck,
  FiDollarSign,
  FiFileText,
  FiMenu,
  FiX,
  FiLogOut,
  FiUser,
  FiSettings,
  FiChevronDown,
  FiChevronRight,
  FiBell,
  FiCreditCard,
  FiBarChart2,
  FiAlertTriangle,
  FiShield,
  FiMessageSquare
} from 'react-icons/fi';

const ROLE_LABELS = {
  admin: 'Administrateur',
  enseignant: 'Enseignant',
  secretaire: 'Secrétaire',
  directeur: 'Directeur',
};

const NavItem = ({ item, isActive, onNavigate }) => {
  const Icon = item.icon;
  const active = isActive(item.href);

  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      className={`group flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
        active
          ? 'bg-white/15 text-white font-semibold shadow-sm ring-1 ring-white/20 backdrop-blur-sm'
          : 'text-white/90 hover:text-white hover:bg-white/10'
      }`}
    >
      <Icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-blue-100' : 'text-blue-200 group-hover:text-white'}`} />
      <span className="truncate">{item.name}</span>
      {active && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-blue-200 shadow-sm shadow-blue-200/50" />}
    </Link>
  );
};

const NavSection = ({ id, title, icon: Icon, isOpen, onToggle, children }) => (
  <div className="mt-1">
    <button
      type="button"
      onClick={() => onToggle(id)}
      className="flex items-center w-full gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-blue-100 hover:text-white transition-colors rounded-lg hover:bg-white/8"
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      <span className="flex-1 text-left">{title}</span>
      <FiChevronRight className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
    </button>
    {isOpen && <div className="mt-0.5 space-y-0.5 pl-1">{children}</div>}
  </div>
);

const NavSubSection = ({ title, icon: Icon, isOpen, onToggle, children }) => (
  <div className="ml-2 border-l border-blue-300/25 pl-2">
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center w-full gap-2 px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-blue-100/80 hover:text-white transition-colors rounded-md"
    >
      {Icon && <Icon className="h-3 w-3" />}
      <span className="flex-1 text-left">{title}</span>
      <FiChevronRight className={`h-3 w-3 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
    </button>
    {isOpen && <div className="mt-0.5 space-y-0.5">{children}</div>}
  </div>
);

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [openSubSection, setOpenSubSection] = useState(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [etablissement, setEtablissement] = useState(null);
  const location = useLocation();
  const { logout, user, hasPermission } = useAuth();

  const closeSidebar = () => setSidebarOpen(false);

  useEffect(() => {
    const fetchEtablissement = async () => {
      try {
        const res = await api.get('/etablissement');
        if (res.data.success && res.data.data) {
          setEtablissement(res.data.data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'établissement:', error);
      }
    };
    fetchEtablissement();
  }, []);

  const dashboardItem = { name: 'Tableau de bord', href: '/app/dashboard', icon: FiHome, permission: null };

  const vieScolaireNavigation = [
    { name: 'Années scolaires', href: '/app/annees-scolaires', icon: FiHome, permission: 'annees-scolaires.view' },
    { name: 'Élèves', href: '/app/eleves', icon: FiUsers, permission: 'eleves.view' },
    { name: 'Parents', href: '/app/parents', icon: FiUsers, permission: 'eleves.view' },
    { name: 'Demandes compte parents', href: '/app/parent-account-requests', icon: FiUserCheck, permission: 'espace-parents.manage' },
    { name: 'Notifier bulletins parents', href: '/app/parent-notify-bulletins', icon: FiBell, permission: 'espace-parents.manage' },
    { name: 'Messagerie parents', href: '/app/parent-messages', icon: FiMessageSquare, permission: 'parent-messages.manage' },
    { name: 'RDV parents', href: '/app/parent-appointments', icon: FiUserCheck, permission: 'parent-rdv.manage' },
    { name: 'Ouverture de classe', href: '/app/ouvertures-classes', icon: FiBookOpen, permission: 'ouvertures-classes.view' },
    { name: 'Inscriptions', href: '/app/inscriptions', icon: FiUserCheck, permission: 'inscriptions.view' },
    { name: 'Evaluations', href: '/app/evaluations', icon: FiFileText, permission: 'evaluations.view' },
    { name: 'Absences', href: '/app/absences', icon: FiFileText, permission: 'absences.view' },
    { name: 'Incidents', href: '/app/incidents', icon: FiFileText, permission: 'incidents.view' },
    { name: 'Relevés de notes', href: '/app/releves-notes', icon: FiFileText, permission: 'releves-notes.view' },
  ];

  const fraisScolariteNavigation = [
    { name: 'Rubriques', href: '/app/frais-scolarite/rubriques', icon: FiFileText, permission: 'frais-scolarite.view' },
    { name: 'Grilles tarifaires', href: '/app/frais-scolarite/grilles', icon: FiCreditCard, permission: 'frais-scolarite.view' },
    { name: 'Encaissements', href: '/app/frais-scolarite/encaissements', icon: FiDollarSign, permission: 'frais-scolarite.view' },
    { name: 'Impayés', href: '/app/frais-scolarite/impayes', icon: FiAlertTriangle, permission: 'frais-scolarite.view' },
    { name: 'Statistiques', href: '/app/frais-scolarite/statistiques', icon: FiBarChart2, permission: 'frais-scolarite.view' },
  ];

  const adminNavigation = [
    { name: 'Utilisateurs', href: '/app/users', icon: FiUsers, permission: 'users.view' },
    { name: 'Rôles & Permissions', href: '/app/roles-permissions', icon: FiShield, permission: 'roles.view' },
    { name: 'Notifications', href: '/app/notifications-admin', icon: FiBell, permission: 'notifications.admin' },
  ];

  const profsNavigation = [
    { name: 'Professeurs', href: '/app/professeurs', icon: FiUsers, permission: 'professeurs.view' },
    { name: 'Niveaux de qualification', href: '/app/niveau-qualif-prof', icon: FiFileText, permission: 'niveau-qualif-prof.view' },
    { name: 'Qualifications', href: '/app/qualifications', icon: FiFileText, permission: 'qualifications.view' },
    { name: 'Qualifications professeurs', href: '/app/prof-qualif', icon: FiFileText, permission: 'prof-qualif.view' },
  ];

  const parentsNavigation = [
    { name: 'Types de parenté', href: '/app/types-parentes', icon: FiSettings, permission: 'types-parentes.view' },
    { name: 'Religions', href: '/app/religions', icon: FiFileText, permission: 'religions.view' },
    { name: 'Professions', href: '/app/professions', icon: FiFileText, permission: 'professions.view' },
  ];

  const classesMatieresNavigation = [
    { name: 'Classes', href: '/app/classes', icon: FiBookOpen, permission: 'classes.view' },
    { name: 'Cycles', href: '/app/cycles', icon: FiBookOpen, permission: 'settings.view' },
    { name: 'Périodes', href: '/app/periodes', icon: FiFileText, permission: 'periodes.view' },
    { name: 'Branches', href: '/app/branches', icon: FiBookOpen, permission: 'branches.view' },
    { name: 'Matières', href: '/app/matieres', icon: FiBookOpen, permission: 'matieres.view' },
    { name: 'Classe-matières', href: '/app/classe-matieres', icon: FiBookOpen, permission: 'classe-matieres.view' },
  ];

  const evaluationsNavigation = [
    { name: 'Types d\'évaluations', href: '/app/type-evaluations', icon: FiFileText, permission: 'type-evaluations.view' },
  ];

  const settingsNavigation = [
    { name: 'Etablissement', href: '/app/etablissement', icon: FiSettings, permission: 'etablissement.view' },
  ];

  const isActive = (path) => location.pathname === path;

  const filterByPermission = (items, hideForTeacher = true) =>
    items.filter((item) => {
      if (hideForTeacher && user?.role === 'enseignant') return false;
      return !item.permission || hasPermission(item.permission);
    });

  const filterVieScolaire = () =>
    vieScolaireNavigation.filter((item) => {
      if (user?.role === 'enseignant') return item.name === 'Ouverture de classe';
      return !item.permission || hasPermission(item.permission);
    });

  const showDashboard = !dashboardItem.permission || hasPermission(dashboardItem.permission);
  const filteredVieScolaireNavigation = filterVieScolaire();
  const filteredFraisScolariteNavigation = filterByPermission(fraisScolariteNavigation);
  const filteredAdminNavigation = filterByPermission(adminNavigation);
  const filteredProfsNavigation = filterByPermission(profsNavigation);
  const filteredParentsNavigation = filterByPermission(parentsNavigation);
  const filteredClassesMatieresNavigation = filterByPermission(classesMatieresNavigation);
  const filteredEvaluationsNavigation = filterByPermission(evaluationsNavigation);
  const filteredSettingsNavigation = filterByPermission(settingsNavigation);

  const hasSettingsSection =
    filteredProfsNavigation.length > 0 ||
    filteredParentsNavigation.length > 0 ||
    filteredClassesMatieresNavigation.length > 0 ||
    filteredEvaluationsNavigation.length > 0 ||
    filteredSettingsNavigation.length > 0;

  useEffect(() => {
    const path = location.pathname;

    if (path.startsWith('/app/frais-scolarite')) {
      setOpenSection('fraisScolarite');
      return;
    }

    if (filteredVieScolaireNavigation.some((item) => item.href === path)) {
      setOpenSection('vieScolaire');
      return;
    }

    if (filteredAdminNavigation.some((item) => item.href === path)) {
      setOpenSection('admin');
      return;
    }

    const profsPaths = filteredProfsNavigation.map((i) => i.href);
    const parentsPaths = filteredParentsNavigation.map((i) => i.href);
    const classesPaths = filteredClassesMatieresNavigation.map((i) => i.href);
    const evalPaths = filteredEvaluationsNavigation.map((i) => i.href);
    const settingsPaths = filteredSettingsNavigation.map((i) => i.href);

    if (
      profsPaths.includes(path) ||
      parentsPaths.includes(path) ||
      classesPaths.includes(path) ||
      evalPaths.includes(path) ||
      settingsPaths.includes(path)
    ) {
      setOpenSection('settings');
      if (profsPaths.includes(path)) setOpenSubSection('profs');
      else if (parentsPaths.includes(path)) setOpenSubSection('parents');
      else if (classesPaths.includes(path)) setOpenSubSection('classesMatieres');
      else if (evalPaths.includes(path)) setOpenSubSection('evaluations');
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const userInitials = `${user?.prenom?.[0] || ''}${user?.nom?.[0] || ''}`.toUpperCase() || '?';
  const roleLabel = ROLE_LABELS[user?.role] || user?.role;

  return (
    <div className="flex h-screen w-full max-w-full bg-slate-50 overflow-hidden">
      {/* Overlay mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — fixed hors flux sur mobile, relative dans le flex sur desktop */}
      <aside
        className={`sidebar-bg fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-white/[0.08] shadow-2xl shadow-black/30 transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:flex-shrink-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        {/* Brand */}
        <div className="sidebar-header relative z-10 flex items-center justify-between h-16 px-4 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 shadow-lg shadow-black/30 ring-1 ring-white/20 flex-shrink-0">
              <FiBookOpen className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">Schola v3</p>
              <p className="text-[10px] text-blue-200/80 truncate">Gestion scolaire</p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeSidebar}
            className="md:hidden p-1.5 rounded-md text-blue-100 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Fermer le menu"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="relative z-10 flex-1 overflow-y-auto sidebar-scroll px-3 py-4 space-y-1">
          {showDashboard && (
            <NavItem item={dashboardItem} isActive={isActive} onNavigate={closeSidebar} />
          )}

          {filteredVieScolaireNavigation.length > 0 && (
            <NavSection
              id="vieScolaire"
              title="Vie scolaire"
              icon={FiUsers}
              isOpen={openSection === 'vieScolaire'}
              onToggle={toggleSection}
            >
              {filteredVieScolaireNavigation.map((item) => (
                <NavItem key={item.name} item={item} isActive={isActive} onNavigate={closeSidebar} />
              ))}
            </NavSection>
          )}

          {filteredFraisScolariteNavigation.length > 0 && (
            <NavSection
              id="fraisScolarite"
              title="Frais de scolarité"
              icon={FiCreditCard}
              isOpen={openSection === 'fraisScolarite'}
              onToggle={toggleSection}
            >
              {filteredFraisScolariteNavigation.map((item) => (
                <NavItem key={item.name} item={item} isActive={isActive} onNavigate={closeSidebar} />
              ))}
            </NavSection>
          )}

          {filteredAdminNavigation.length > 0 && (
            <NavSection
              id="admin"
              title="Administration"
              icon={FiShield}
              isOpen={openSection === 'admin'}
              onToggle={toggleSection}
            >
              {filteredAdminNavigation.map((item) => (
                <NavItem key={item.name} item={item} isActive={isActive} onNavigate={closeSidebar} />
              ))}
            </NavSection>
          )}

          {hasSettingsSection && (
            <NavSection
              id="settings"
              title="Paramètres"
              icon={FiSettings}
              isOpen={openSection === 'settings'}
              onToggle={toggleSection}
            >
              {filteredProfsNavigation.length > 0 && (
                <NavSubSection
                  title="Professeurs"
                  icon={FiUsers}
                  isOpen={openSubSection === 'profs'}
                  onToggle={() => setOpenSubSection(openSubSection === 'profs' ? null : 'profs')}
                >
                  {filteredProfsNavigation.map((item) => (
                    <NavItem key={item.name} item={item} isActive={isActive} onNavigate={closeSidebar} />
                  ))}
                </NavSubSection>
              )}

              {filteredParentsNavigation.length > 0 && (
                <NavSubSection
                  title="Parents"
                  icon={FiUser}
                  isOpen={openSubSection === 'parents'}
                  onToggle={() => setOpenSubSection(openSubSection === 'parents' ? null : 'parents')}
                >
                  {filteredParentsNavigation.map((item) => (
                    <NavItem key={item.name} item={item} isActive={isActive} onNavigate={closeSidebar} />
                  ))}
                </NavSubSection>
              )}

              {filteredClassesMatieresNavigation.length > 0 && (
                <NavSubSection
                  title="Classes & matières"
                  icon={FiBookOpen}
                  isOpen={openSubSection === 'classesMatieres'}
                  onToggle={() => setOpenSubSection(openSubSection === 'classesMatieres' ? null : 'classesMatieres')}
                >
                  {filteredClassesMatieresNavigation.map((item) => (
                    <NavItem key={item.name} item={item} isActive={isActive} onNavigate={closeSidebar} />
                  ))}
                </NavSubSection>
              )}

              {filteredEvaluationsNavigation.length > 0 && (
                <NavSubSection
                  title="Évaluations"
                  icon={FiFileText}
                  isOpen={openSubSection === 'evaluations'}
                  onToggle={() => setOpenSubSection(openSubSection === 'evaluations' ? null : 'evaluations')}
                >
                  {filteredEvaluationsNavigation.map((item) => (
                    <NavItem key={item.name} item={item} isActive={isActive} onNavigate={closeSidebar} />
                  ))}
                </NavSubSection>
              )}

              {filteredSettingsNavigation.map((item) => (
                <NavItem key={item.name} item={item} isActive={isActive} onNavigate={closeSidebar} />
              ))}
            </NavSection>
          )}
        </nav>

        {/* Footer sidebar */}
        <div className="sidebar-footer relative z-10 flex-shrink-0 px-4 py-3">
          <p className="text-[10px] text-blue-200/70 text-center">© Schola v3</p>
        </div>
      </aside>

      {/* Main content — pleine largeur sur mobile */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 w-full">
        {/* Header */}
        <header className="flex-shrink-0 bg-white border-b border-slate-200/80 shadow-sm z-20">
          {/* Ligne 1 : outils + actions utilisateur */}
          <div className="flex items-center h-14 sm:h-16 px-3 sm:px-4 lg:px-6 gap-2 sm:gap-4 min-w-0">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 -ml-1 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0"
              aria-label="Ouvrir le menu"
            >
              <FiMenu className="h-5 w-5" />
            </button>

            <div className="flex-shrink-0 min-w-0">
              <YearSelector />
            </div>

            {/* Établissement inline — grands écrans uniquement (xl+) */}
            <div className="flex-1 hidden xl:flex items-center justify-center min-w-0 px-2">
              {etablissement && (
                <div className="flex items-center gap-3 max-w-lg min-w-0">
                  {etablissement.logo && (
                    <img
                      src={`/uploads/${etablissement.logo}`}
                      alt="Logo établissement"
                      className="h-9 w-auto object-contain flex-shrink-0"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  {etablissement.denomination && (
                    <span className="text-sm font-semibold text-slate-800 truncate">
                      {etablissement.denomination}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Droite : actions utilisateur */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-auto">
              <NotificationDropdown />

              <div className="h-8 w-px bg-slate-200 hidden sm:block" />

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="flex items-center gap-2.5 pl-1 pr-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                >
                  <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-xs font-semibold text-white shadow-sm">
                    {userInitials}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-slate-800 leading-tight">
                      {user?.prenom} {user?.nom}
                    </p>
                    <p className="text-[11px] text-slate-500 leading-tight">{roleLabel}</p>
                  </div>
                  <FiChevronDown className={`h-4 w-4 text-slate-400 transition-transform hidden sm:block ${userDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {userDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUserDropdownOpen(false)}
                      aria-hidden="true"
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-1.5 z-20 animate-fadeIn">
                      <div className="px-4 py-2.5 border-b border-slate-100 sm:hidden">
                        <p className="text-sm font-medium text-slate-800">{user?.prenom} {user?.nom}</p>
                        <p className="text-xs text-slate-500">{roleLabel}</p>
                      </div>
                      <Link
                        to="/app/profile"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <FiUser className="h-4 w-4 text-slate-400" />
                        <span>Mon profil</span>
                      </Link>
                      <div className="border-t border-slate-100 my-1" />
                      <button
                        type="button"
                        onClick={() => {
                          setUserDropdownOpen(false);
                          logout();
                        }}
                        className="flex items-center gap-3 px-4 py-2.5 w-full text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <FiLogOut className="h-4 w-4" />
                        <span>Déconnexion</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Ligne 2 : établissement — tablette / écrans intermédiaires (md → xl), ex. 915×412 */}
          {etablissement?.denomination && (
            <div className="header-etablissement-row hidden md:flex xl:hidden items-center justify-center gap-2 px-3 sm:px-4 py-1.5 border-t border-slate-100 bg-slate-50/50 min-w-0">
              {etablissement.logo && (
                <img
                  src={`/uploads/${etablissement.logo}`}
                  alt=""
                  className="h-6 w-auto object-contain flex-shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <span className="text-xs sm:text-sm font-semibold text-slate-800 text-center leading-snug line-clamp-2">
                {etablissement.denomination}
              </span>
            </div>
          )}
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 lg:p-6 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
